import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { DonorBadge } from "../lib/badges";
import { LiveBadge } from "./Eventi";
import { ArrowLeft, Send, MessageSquare, CalendarClock, Radio, Timer, Gavel, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { twitchParents } from "../lib/twitch";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

function ytId(url) {
  if (!url) return "";
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/);
  return m ? m[1] : url;
}

function InternalChat({ slug, user }) {
  const { login } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const lastAt = useRef("");
  const boxRef = useRef(null);
  const isStaff = !!user?.is_staff;
  const [timeouts, setTimeouts] = useState([]);
  const [muteFor, setMuteFor] = useState(null);

  useEffect(() => {
    if (!isStaff) return;
    api.get(`/game-events/${slug}/chat/timeouts`).then(({ data }) => setTimeouts(data.timeouts || [])).catch(() => {});
  }, [isStaff, slug]);

  const moderate = async (targetLogin, action, minutes) => {
    try {
      await api.post("/game-events/chat/moderate", { login: targetLogin, action, minutes });
      if (action === "timeout") toast.success(`${targetLogin} silenziato per ${minutes} minuti.`);
      if (action === "untimeout") toast.success(`${targetLogin} può di nuovo scrivere in chat.`);
      if (action === "ban") toast.success(`${targetLogin} bannato dal sito.`);
      const { data } = await api.get(`/game-events/${slug}/chat/timeouts`);
      setTimeouts(data.timeouts || []);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d === "cannot_moderate_staff" ? "Non puoi moderare un membro dello staff." : "Azione non riuscita.");
    } finally {
      setMuteFor(null);
    }
  };
  const timedOutLogins = new Set(timeouts.map((t) => t.login));

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/game-events/${slug}/chat`, { params: lastAt.current ? { after: lastAt.current } : {} });
        if (stop || !data.messages.length) return;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          return [...prev, ...data.messages.filter((m) => !ids.has(m.id))].slice(-120);
        });
        lastAt.current = data.messages[data.messages.length - 1].at;
      } catch { /* polling silenzioso */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { stop = true; clearInterval(id); };
  }, [slug]);

  useEffect(() => {
    boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      await api.post(`/game-events/${slug}/chat`, { text: value });
      setText("");
      const { data } = await api.get(`/game-events/${slug}/chat`);
      setMessages(data.messages.slice(-120));
      lastAt.current = data.messages.length ? data.messages[data.messages.length - 1].at : lastAt.current;
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.startsWith("muted:")) {
        toast.error(`Sei stato silenziato dallo staff: potrai scrivere tra circa ${detail.split(":")[1]} minuti.`);
        return;
      }
      const MOD_MSGS = {
        slow_down: "Rallenta: stai scrivendo troppo in fretta.",
        zalgo_blocked: "Testo non valido: niente caratteri glitciati.",
        banned_word: "Messaggio bloccato: contiene parole vietate.",
        link_blocked: "I link sono disattivati in questa chat.",
        caps_blocked: "Troppo maiuscolo: scrivi normalmente.",
        spam_blocked: "Messaggio bloccato come spam.",
        empty_message: "Scrivi qualcosa prima di inviare.",
      };
      if (err?.response?.status === 401) toast.error("Accedi per chattare.");
      else toast.error(MOD_MSGS[detail] || "Messaggio non inviato.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] flex flex-col h-[540px]" data-testid="internal-chat">
      <div className="px-4 py-3 border-b border-[#1A1A1A] flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5 text-wardogs" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Chat della community</span>
      </div>
      <div ref={boxRef} className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3" data-lenis-prevent data-testid="internal-chat-messages">
        {messages.length === 0 && (
          <p className="font-mono text-[11px] text-[#4A4D51] text-center mt-8">Nessun messaggio ancora — rompi il ghiaccio tu.</p>
        )}
        {messages.map((m) => (
          <div key={m.id} data-testid={`chat-msg-${m.id}`}>
            <div className="flex items-start gap-2.5">
              {m.avatar ? (
                <img src={m.avatar} alt="" className="w-6 h-6 chamfer-sm object-cover shrink-0 mt-0.5" />
              ) : (
                <span className="w-6 h-6 chamfer-sm bg-[#1A1A1A] shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono text-[11px] font-bold text-white">{m.name}</span>
                  {m.donated && <DonorBadge size="w-3 h-3" testid={`chat-donor-${m.id}`} />}
                  <span className="font-mono text-[8px] text-[#4A4D51]">{new Date(m.at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                  {isStaff && m.login !== user.login && (
                    <span className="inline-flex items-center gap-1 ml-auto">
                      {timedOutLogins.has(m.login) ? (
                        <button
                          onClick={() => moderate(m.login, "untimeout")}
                          title="Riabilita in chat"
                          className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.1em] text-wardogs hover:text-wardogs-hover transition-colors duration-200"
                          data-testid={`chat-untimeout-${m.login}`}
                        >
                          <Undo2 className="w-3 h-3" /> Riabilita
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => setMuteFor(muteFor === m.id ? null : m.id)}
                            title="Silenzia temporaneamente"
                            className="text-[#4A4D51] hover:text-wardogs transition-colors duration-200"
                            data-testid={`chat-mute-${m.login}`}
                          >
                            <Timer className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { if (window.confirm(`Bannare ${m.name} dal sito? Non potrà più accedere né scrivere.`)) moderate(m.login, "ban"); }}
                            title="Banna dal sito"
                            className="text-[#4A4D51] hover:text-valkyra transition-colors duration-200"
                            data-testid={`chat-ban-${m.login}`}
                          >
                            <Gavel className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </span>
                  )}
                </p>
                <p className="text-sm text-steel leading-snug break-words">{m.text}</p>
              </div>
            </div>
            {muteFor === m.id && (
              <div className="flex gap-1.5 mt-1.5 ml-9" data-testid={`chat-mute-menu-${m.login}`}>
                {[10, 30, 60].map((min) => (
                  <button
                    key={min}
                    onClick={() => moderate(m.login, "timeout", min)}
                    className="chamfer-sm border border-wardogs/50 text-wardogs hover:bg-wardogs hover:text-[#050505] font-mono text-[9px] uppercase tracking-[0.1em] font-bold px-2.5 py-1 transition-colors duration-200"
                    data-testid={`chat-mute-${min}-${m.login}`}
                  >
                    {min} min
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {user ? (
        <form onSubmit={send} className="p-3 border-t border-[#1A1A1A] flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={300}
            placeholder="Scrivi alla community…"
            className="flex-1 bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-3 py-2.5 font-mono text-xs text-white transition-colors duration-300"
            data-testid="internal-chat-input"
          />
          <button type="submit" disabled={busy || !text.trim()} className="chamfer-sm bg-wardogs hover:bg-wardogs-hover disabled:opacity-40 text-[#050505] px-4 transition-colors duration-300" data-testid="internal-chat-send" aria-label="Invia">
            <Send className="w-4 h-4" />
          </button>
        </form>
      ) : (
        <div className="p-3 border-t border-[#1A1A1A]">
          <button onClick={login} className="w-full chamfer-sm border border-wardogs text-wardogs hover:bg-wardogs hover:text-[#050505] py-3 font-mono text-[10px] uppercase tracking-[0.2em] font-bold transition-colors duration-300" data-testid="internal-chat-login">
            Accedi per chattare
          </button>
        </div>
      )}
    </div>
  );
}

export default function EventRoom() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  const [chatMode, setChatMode] = useState("twitch");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = () =>
      api.get("/game-events")
        .then(({ data }) => {
          const found = data.events.find((e) => e.slug === slug);
          setEv(found || null);
          setNotFound(!found);
          setChatMode(data.chat_mode || "twitch");
        })
        .catch(() => setNotFound(true));
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [slug]);

  const parentParams = twitchParents();
  const showTwitchChat = ev?.platform === "twitch" && chatMode === "twitch";
  const secondsLeft = ev ? Math.max(0, ev.seconds_to) : 0;
  const dd = Math.floor(secondsLeft / 86400);
  const hh = Math.floor((secondsLeft % 86400) / 3600);
  const mm = Math.floor((secondsLeft % 3600) / 60);

  return (
    <main className="pt-32 pb-28" data-testid="event-room">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Link to="/eventi" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel hover:text-wardogs transition-colors duration-300 mb-8" data-testid="event-room-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Tutti gli eventi
        </Link>

        {notFound ? (
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-10 text-center" data-testid="event-room-404">
            <CalendarClock className="w-8 h-8 text-[#2A2A2A] mx-auto mb-4" />
            <p className="font-mono text-xs text-steel">Evento non trovato o non più in programma.</p>
          </div>
        ) : !ev ? (
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] h-96 animate-pulse" />
        ) : (
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {ev.is_live && <LiveBadge testid="event-room-live" />}
              {ev.estimated && <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#4A4D51]">data stimata</span>}
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] mb-3" data-testid="event-room-title">{ev.name}</h1>
            <p className="font-mono text-xs text-steel uppercase tracking-[0.15em] mb-3" data-testid="event-room-date">
              {new Date(ev.date_it).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}
            </p>
            {!ev.is_live && !ev.is_past && (
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] mb-8" style={{ color: ev.color }} data-testid="event-room-countdown">
                Inizia tra {dd > 0 && `${pad(dd)}g : `}{pad(hh)}h : {pad(mm)}m
              </p>
            )}
            {ev.is_live && <div className="mb-8" />}

            <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
              <div>
                <div className="chamfer border border-[#1A1A1A] overflow-hidden bg-[#0C0C0C] aspect-video" data-testid="event-room-player">
                  {ev.is_live || ev.seconds_to <= 1800 ? (
                    ev.platform === "twitch" ? (
                      <iframe
                        src={`https://player.twitch.tv/?channel=${ev.channel}&${parentParams}&autoplay=true&muted=true`}
                        title={`${ev.name} — diretta`}
                        className="w-full h-full border-0"
                        allowFullScreen
                        data-testid="event-twitch-player"
                      />
                    ) : (
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${ytId(ev.youtube_url)}?autoplay=1&mute=1`}
                        title={`${ev.name} — diretta`}
                        className="w-full h-full border-0"
                        allow="autoplay; encrypted-media; fullscreen"
                        allowFullScreen
                        data-testid="event-youtube-player"
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-8" data-testid="event-player-standby">
                      <Radio className="w-8 h-8 mb-5" style={{ color: ev.color }} />
                      {ev.is_past ? (
                        <>
                          <p className="font-display text-3xl md:text-4xl uppercase leading-none mb-2">Evento concluso</p>
                          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A4D51]">Ci vediamo alla prossima edizione</p>
                        </>
                      ) : (
                        <>
                          <p className="font-display text-3xl md:text-4xl uppercase leading-none mb-3">La diretta si attiva qui</p>
                          <p className="font-mono text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: ev.color }}>Il player appare da solo 30 minuti prima dell'inizio</p>
                          <p className="font-mono text-[10px] text-[#4A4D51] uppercase tracking-[0.15em] max-w-md leading-relaxed">Niente link esterni: quando lo show parte, si guarda tutto da questa pagina</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {ev.platform === "twitch" && (
                  <div className="chamfer border border-[#9146FF]/40 bg-[#0C0C0C] p-6 mt-6" data-testid="event-drops-box">
                    <p className="hud-label mb-2 text-[#9146FF]">// DROP TWITCH ATTIVI</p>
                    <p className="font-mono text-[11px] text-white leading-relaxed mb-4">
                      I Drop si accumulano anche guardando da questa pagina: il player ufficiale Twitch conta la tua visione. Tre regole per non perderli:
                    </p>
                    <ol className="flex flex-col gap-2.5 mb-5" data-testid="event-drops-steps">
                      <li className="flex gap-3 items-start">
                        <span className="chamfer-sm border border-[#9146FF]/50 text-[#9146FF] font-mono text-[10px] font-bold px-2 py-0.5 shrink-0">1</span>
                        <span className="font-mono text-[11px] text-steel leading-relaxed">Accedi a <span className="text-white">Twitch in questo browser</span>: il player usa la tua sessione — da sloggato la visione non conta</span>
                      </li>
                      <li className="flex gap-3 items-start">
                        <span className="chamfer-sm border border-[#9146FF]/50 text-[#9146FF] font-mono text-[10px] font-bold px-2 py-0.5 shrink-0">2</span>
                        <span className="font-mono text-[11px] text-steel leading-relaxed">Collega l'account Twitch all'<span className="text-white">account gioco</span> dalla pagina della campagna Drop</span>
                      </li>
                      <li className="flex gap-3 items-start">
                        <span className="chamfer-sm border border-[#9146FF]/50 text-[#9146FF] font-mono text-[10px] font-bold px-2 py-0.5 shrink-0">3</span>
                        <span className="font-mono text-[11px] text-steel leading-relaxed">Tieni questa <span className="text-white">scheda in primo piano</span> col player in riproduzione — può restare anche mutato, conta lo stesso</span>
                      </li>
                    </ol>
                    <a
                      href={`https://www.twitch.tv/${ev.channel}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 chamfer-sm border border-[#9146FF] text-[#9146FF] hover:bg-[#9146FF] hover:text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] font-bold transition-colors duration-300"
                      data-testid="event-drops-twitch-link"
                    >
                      Apri su Twitch (di riserva)
                    </a>
                  </div>
                )}
                {ev.description && (
                  <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 mt-6" data-testid="event-room-desc">
                    <p className="hud-label mb-3 text-wardogs">// BRIEFING</p>
                    <p className="text-sm text-steel leading-relaxed">{ev.description}</p>
                  </div>
                )}
              </div>

              {showTwitchChat ? (
                <div className="chamfer border border-[#1A1A1A] overflow-hidden bg-[#0C0C0C] h-[540px]" data-testid="twitch-chat-box">
                  <div className="px-4 py-3 border-b border-[#1A1A1A] flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-[#9146FF]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Chat Twitch</span>
                  </div>
                  <iframe
                    src={`https://www.twitch.tv/embed/${ev.channel}/chat?${parentParams}&darkpopout`}
                    title="Chat Twitch"
                    className="w-full border-0"
                    style={{ height: "calc(100% - 46px)" }}
                    data-testid="event-twitch-chat"
                  />
                </div>
              ) : (
                <InternalChat slug={slug} user={user} />
              )}
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
